<script lang="ts">
  /**
   * The room before the match (`docs/decisions/0054`). It is shown *inside*
   * the room, because a code for a room nobody is sitting in is a code for a
   * room the server will collect. Both players and the duel they are about
   * to play are here, so nobody readies up into a variant they did not
   * expect.
   *
   * - **A room with a person in it, or a seat for one**: the shell's
   *   `LobbyTemplate`, locked (`dismissible: false`): the code to hand over
   *   (Copy, Share, and the link under them), the seats with who is ready,
   *   which duel this is, the curse loadout when the variant has one, Ready
   *   up, and Leave room.
   * - **Against the computer** there is no code (nobody can be invited into
   *   the other seat) and no Ready (the room readies on the player's behalf,
   *   `DuelScreen.svelte`): the same template with no `code` (optional since
   *   shell 1.3.0, golden `lobby-computer`), its status line saying the match
   *   is starting, or that a loadout is still to pick.
   *
   * **The code is the server's word.** The room is entered before the server
   * has said its code (its `welcome` message), so for a moment `code` is
   * empty. Until it arrives the code block is `pending` (shell 1.4.0): a calm
   * "Waiting for the code…" where the code goes, Copy and Share disabled, and
   * no link drawn, since a link built from an empty code is `/join/`, which
   * opens the app on Home rather than in this room (`docs/decisions/0055`,
   * `0057`).
   *
   * The lobby is **the server's**: it appears while the room's phase is the
   * lobby and goes when the match starts. Escape and a press beside them do
   * nothing; Back is reported (`onBackAttempt`), and the room decides (in the
   * lobby it simply leaves, `nav/leave-question.ts`). Presentational:
   * everything arrives as a prop from the server's view of the room, and
   * every press is reported.
   */
  import { LobbyTemplate, Note } from '@artibens/app-shell';
  import type { LobbySeat, SheetBackAttempt } from '@artibens/app-shell';
  import { CURSE_LOADOUT_SIZE } from '@interlock/protocol';
  import type { RoomVariant } from '@interlock/protocol';
  import type { CurseEffect } from '@interlock/sim';

  import { explainAiHandicap, explainVariant } from '../../copy/explanations.js';
  import GameGlyph from '../../glyphs/GameGlyph.svelte';
  import { copyText } from '../../net/copy-text.js';
  import { joinLink } from '../../net/share.js';
  import LoadoutPicker from './LoadoutPicker.svelte';

  interface Seat {
    readonly seat: number;
    readonly name: string;
    readonly ready: boolean;
    readonly connected?: boolean;
    readonly bot: unknown;
  }

  interface Props {
    /** The room code, or an empty string before the server has said it. */
    readonly code: string;
    readonly variant: RoomVariant;
    readonly variantLabel: string;
    readonly configLabel: string;
    readonly seats: readonly Seat[];
    readonly mySeat: number | null;
    /** The other seat is the server's own opponent. */
    readonly vsAi: boolean;
    readonly handicapEnabled: boolean;
    /** The variant has a curse loadout to pick. */
    readonly curses: boolean;
    readonly loadout: readonly CurseEffect[];
    /** This seat has a loadout on the server already. */
    readonly loadoutSent: boolean;
    readonly loadoutComplete: boolean;
    readonly ready: boolean;
    readonly onToggleCurse: (effect: CurseEffect) => void;
    readonly onToggleReady: () => void;
    readonly onLeave: () => void;
    /** Back, Escape or a press beside the lobby: what it was. */
    readonly onBackAttempt: (reason: SheetBackAttempt) => void;
  }
  const {
    code,
    variant,
    variantLabel,
    configLabel,
    seats,
    mySeat,
    vsAi,
    handicapEnabled,
    curses,
    loadout,
    loadoutSent,
    loadoutComplete,
    ready,
    onToggleCurse,
    onToggleReady,
    onLeave,
    onBackAttempt,
  }: Props = $props();

  const other = $derived(seats.find((seat) => seat.seat !== mySeat && seat.name !== '') ?? null);

  const waitingLine = $derived.by(() => {
    if (other === null) return 'Waiting for somebody to join…';
    if (ready && !other.ready) return `Waiting for ${other.name}…`;
    if (!ready && other.ready) return `${other.name} is ready.`;
    return 'Both here. Ready up when you are.';
  });

  const lobbySeats = $derived<LobbySeat[]>(
    seats.map((player) => {
      const empty = player.name === '';
      return {
        key: String(player.seat),
        name: empty ? 'Empty seat' : player.name,
        you: player.seat === mySeat,
        connected: !empty && player.connected !== false,
        // The server says this seat is its own; a name a person typed could
        // only look like it.
        bot: player.bot !== null,
        empty,
        // Against the computer there is no ready step to show.
        ...(empty ? { badge: 'Waiting' } : vsAi ? {} : { ready: player.ready }),
        testid: 'duel-roster-seat',
      };
    }),
  );

  /* --- handing the code over ------------------------------------------- */

  /** Whether the code is still on its way from the server (see the header). */
  const codePending = $derived(code === '');
  const link = $derived(joinLink(code, window.location.origin));
  const canShare = $derived(
    typeof navigator !== 'undefined' && typeof navigator.share === 'function',
  );

  /** What the last Copy or Share did, said under the buttons. */
  let copyNote: string | null = $state(null);

  async function copy(text: string, what: string): Promise<void> {
    copyNote = (await copyText(text))
      ? what
      : 'Could not reach the clipboard. Read the code out instead.';
  }

  /** The native share sheet with the join link where there is one (a phone, over https); the link copied where not. */
  async function share(): Promise<void> {
    if (!canShare) {
      await copy(link, 'Link copied');
      return;
    }
    try {
      await navigator.share({
        title: 'Interlock',
        text: `Join my Interlock room. The code is ${code}`,
        url: link,
      });
    } catch {
      // The sheet was dismissed, or the browser refused it. Not an error worth
      // showing: the code and the Copy button are still right there.
    }
  }

  /** Back is the room's to answer; Escape and a press beside the lobby do nothing to a lobby the server owns. */
  function onAttempt(reason: SheetBackAttempt): void {
    if (reason === 'back') onBackAttempt(reason);
  }
</script>

{#snippet rules()}
  <Note size="sm" centered>{explainVariant(variant)}</Note>
  {#if vsAi}
    <Note size="sm" centered>{explainAiHandicap()}</Note>
  {:else if handicapEnabled}
    <Note size="sm" centered>
      Handicap is on: whoever is behind gets a small boost, shown to both of you with a <span
        class="diamond"
        role="img"
        aria-label="diamond"><GameGlyph name="diamond" size={9} /></span
      >.
    </Note>
  {/if}
{/snippet}

{#snippet loadoutPicker()}
  <LoadoutPicker {loadout} {loadoutSent} onToggle={onToggleCurse} />
{/snippet}

<LobbyTemplate
  title={vsAi ? 'Play the computer' : 'Waiting to start'}
  code={vsAi
    ? undefined
    : {
        value: code,
        label: 'Share this code',
        pending: codePending,
        link,
        copyLabel: 'Copy',
        shareLabel: 'Share',
        oncopy: () => void copy(code, 'Code copied'),
        onshare: () => void share(),
        note: copyNote,
      }}
  note={vsAi
    ? undefined
    : 'Read the code out, or send the link. The room opens when you both are in it.'}
  seats={lobbySeats}
  chips={[
    // The chip that names the room is the strong one (the house lobby's rule).
    { key: 'variant', label: variantLabel, tone: 'strong', testid: 'duel-variant-chip' },
    { key: 'config', label: configLabel },
  ]}
  {rules}
  extras={curses ? loadoutPicker : undefined}
  primary={vsAi
    ? undefined
    : {
        label: ready ? 'Ready · waiting' : 'Ready up',
        onclick: onToggleReady,
        disabled: curses && !loadoutComplete,
        testid: 'duel-ready',
      }}
  status={[
    vsAi
      ? {
          text:
            curses && !loadoutComplete
              ? `Pick ${String(CURSE_LOADOUT_SIZE)} curses to start`
              : 'Starting…',
          testid: 'duel-starting',
        }
      : { text: waitingLine, testid: 'duel-waiting' },
  ]}
  leave={{ label: 'Leave room', onclick: onLeave, testid: 'duel-lobby-leave' }}
  dismissible={false}
  onbackattempt={onAttempt}
  youLabel="You"
  botLabel="Computer"
  codeTestids={{
    code: 'room-code',
    copy: 'copy-code',
    share: 'copy-link',
    link: 'join-link',
    note: 'copied-notice',
  }}
  testids={{ root: 'duel-lobby', seats: 'duel-roster', chips: 'duel-lobby-variant' }}
/>

<style>
  /* The handicap's mark, inline in the sentence that names it. */
  .diamond {
    display: inline-block;
    color: var(--ui-accent);
  }
</style>
